// ===================================================
// src/components/FactoryShipments.js
// Multi-SKU Factory → Warehouse Shipments Management
// ===================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./FactoryShipments.css";

export default function FactoryShipments() {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    serialBoxes: "",
    fromFactory: "",
    toWarehouse: "",
    shippingCompany: "",
    trackingNumber: "",
    shipDate: "",
  });

  // 🧱 multiple SKU lines
  const [skuList, setSkuList] = useState([{ sku: "", quantity: "" }]);

  const API_URL = `${process.env.REACT_APP_API_URL}/api/factory-shipments`;
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleSelect = (id) => {
  setSelectedIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  );
};
const handleBulkDelete = async () => {
  if (selectedIds.length === 0) {
    alert("Please select at least one shipment to delete.");
    return;
  }

  if (!window.confirm(`Delete ${selectedIds.length} selected shipments?`)) return;

  for (const id of selectedIds) {
    await fetch(`${API_URL}/${id}`, { method: "DELETE" });
  }

  setSelectedIds([]);
  fetchShipments();
};


  const fetchShipments = async () => {
    const res = await fetch(API_URL);
    const data = await res.json();
    if (data.success) setShipments(data.results);
  };

  useEffect(() => {
    fetchShipments();
  }, []);

  // 🧩 add/remove SKU rows
  const addSkuRow = () => setSkuList([...skuList, { sku: "", quantity: "" }]);
  const removeSkuRow = (index) => setSkuList(skuList.filter((_, i) => i !== index));
  const updateSkuRow = (index, field, value) => {
    const updated = [...skuList];
    updated[index][field] = value;
    setSkuList(updated);
  };

  // 🚀 handle form submission
const handleSubmit = async (e) => {
  e.preventDefault();

  const destination = formData.toWarehouse;
  if (!destination || !formData.shipDate) return;

  const normalizeSkuForDestination = (sku, dest) => {
    let normalizedSku = sku.toLowerCase();
    const isAmazon = dest.startsWith("amazon_");

    if (isAmazon) {
      if (dest === "amazon_us") normalizedSku = normalizedSku.replace(/^us_/, "us_amazon_");
      if (dest === "amazon_uk") normalizedSku = normalizedSku.replace(/^uk_/, "uk_amazon_");
    } else {
      if (dest === "tive" && normalizedSku.startsWith("us_")) {
        normalizedSku = normalizedSku.replace("us_", "uk_");
      }
      if (dest === "itb" && normalizedSku.startsWith("uk_")) {
        normalizedSku = normalizedSku.replace("uk_", "us_");
      }
    }
    return normalizedSku;
  };

  const shipDateObj = new Date(formData.shipDate + "T00:00:00");
  const arrivalDays = ["tive", "amazon_uk"].includes(destination) ? 7 : 6;
  const arrivalDate = new Date(shipDateObj);
  arrivalDate.setDate(shipDateObj.getDate() + arrivalDays);
  const arrivalISO = arrivalDate.toISOString().split("T")[0];

  if (editId) {
    const oldShipment = shipments.find((s) => s.id === editId);

    await fetch(`${API_URL}/${editId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        serial_box: "",
        sku: skuList[0].sku,
        from_factory: formData.fromFactory,
        to_warehouse: formData.toWarehouse,
        shipping_company: formData.shippingCompany,
        tracking_number: formData.trackingNumber,
        ship_date: formData.shipDate,
        quantity: skuList[0].quantity,
      }),
    });

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/manual-overrides`);
      const json = await res.json();
      const overrides = json || {};

      if (oldShipment) {
        const oldShipDate = new Date(oldShipment.ship_date);
        const oldArrivalDays = ["tive", "amazon_uk"].includes(oldShipment.to_warehouse) ? 7 : 6;
        const oldArrival = new Date(oldShipDate);
        oldArrival.setDate(oldShipDate.getDate() + oldArrivalDays);
        const oldArrivalISO = oldArrival.toISOString().split("T")[0];

        const oldNormalizedSku = normalizeSkuForDestination(
          oldShipment.sku,
          oldShipment.to_warehouse
        );

        if (overrides[oldArrivalISO] && overrides[oldArrivalISO][oldNormalizedSku] != null) {
          delete overrides[oldArrivalISO][oldNormalizedSku];
          if (Object.keys(overrides[oldArrivalISO]).length === 0) {
            delete overrides[oldArrivalISO];
          }
        }
      }

      const newNormalizedSku = normalizeSkuForDestination(skuList[0].sku, destination);
      if (!overrides[arrivalISO]) overrides[arrivalISO] = {};
      overrides[arrivalISO][newNormalizedSku] = Number(skuList[0].quantity);

      await fetch(`${process.env.REACT_APP_API_URL}/api/manual-overrides`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(overrides),
      });
    } catch (err) {
      console.error("Failed to update manual overrides:", err);
    }
  } else {
    for (const item of skuList) {
      if (!item.sku || !item.quantity) continue;

      await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          serial_box: "",
          sku: item.sku,
          from_factory: formData.fromFactory,
          to_warehouse: formData.toWarehouse,
          shipping_company: formData.shippingCompany,
          tracking_number: formData.trackingNumber,
          ship_date: formData.shipDate,
          quantity: item.quantity,
        }),
      });
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/manual-overrides`);
      const json = await res.json();
      const overrides = json || {};

      if (!overrides[arrivalISO]) overrides[arrivalISO] = {};

      for (const item of skuList) {
        if (!item.sku || !item.quantity) continue;
        const normalizedSku = normalizeSkuForDestination(item.sku, destination);
        overrides[arrivalISO][normalizedSku] =
          (overrides[arrivalISO][normalizedSku] || 0) + Number(item.quantity);
      }

      await fetch(`${process.env.REACT_APP_API_URL}/api/manual-overrides`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(overrides),
      });
    } catch (err) {
      console.error("Failed to update manual overrides on create:", err);
    }
  }

  setShowForm(false);
  setEditId(null);
  setFormData({
    serialBoxes: "",
    fromFactory: "",
    toWarehouse: "",
    shippingCompany: "",
    trackingNumber: "",
    shipDate: "",
  });
  setSkuList([{ sku: "", quantity: "" }]);
  fetchShipments();
};


   const handleConfirm = async (id) => {
    const chosenDate = prompt("📅 Enter delivery date (YYYY-MM-DD):", new Date().toISOString().split("T")[0]);
    if (!chosenDate) {
      alert("Confirmation cancelled.");
      return;
    }

    const parsed = new Date(chosenDate);
    if (isNaN(parsed.getTime())) {
      alert("Invalid date format. Please use YYYY-MM-DD.");
      return;
    }

    await fetch(`${API_URL}/${id}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ delivered_date: parsed.toISOString() }),
    });

    fetchShipments();
  };


  const handleEdit = (shipment) => {
    const dateObj = new Date(shipment.ship_date);
    const localDate = new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];

    setEditId(shipment.id);
    setFormData({
      serialBoxes: shipment.serial_box || "",
      fromFactory: shipment.from_factory || "",
      toWarehouse: shipment.to_warehouse || "",
      shippingCompany: shipment.shipping_company || "",
      trackingNumber: shipment.tracking_number || "",
      shipDate: localDate,
    });
    setSkuList([{ sku: shipment.sku, quantity: shipment.quantity }]);
    setShowForm(true);
  };



const handleDelete = async (id) => {
  if (!window.confirm("Are you sure you want to delete this shipment?")) return;

  const shipment = shipments.find((s) => s.id === id);
  if (!shipment) return;

  const shipDate = new Date(shipment.ship_date);
  const arrivalDays = ["tive", "amazon_uk"].includes(shipment.to_warehouse) ? 7 : 6;
  const arrivalDate = new Date(shipDate);
  arrivalDate.setDate(shipDate.getDate() + arrivalDays);
  const arrivalISO = arrivalDate.toISOString().split("T")[0];

  let normalizedSku = shipment.sku.toLowerCase();
  const dest = shipment.to_warehouse;
  const isAmazon = dest.startsWith("amazon_");

  if (isAmazon) {
    if (dest === "amazon_us") normalizedSku = normalizedSku.replace(/^us_/, "us_amazon_");
    if (dest === "amazon_uk") normalizedSku = normalizedSku.replace(/^uk_/, "uk_amazon_");
  } else {
    if (dest === "tive" && normalizedSku.startsWith("us_"))
      normalizedSku = normalizedSku.replace("us_", "uk_");
    if (dest === "itb" && normalizedSku.startsWith("uk_"))
      normalizedSku = normalizedSku.replace("uk_", "us_");
  }

  await fetch(`${API_URL}/${id}`, { method: "DELETE" });

  try {
    await fetch(`${process.env.REACT_APP_API_URL}/api/manual-overrides`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sku: normalizedSku,
        arrivalDate: arrivalISO,
      }),
    });
    console.log(`✅ Deleted ${normalizedSku} from manualStockOverrides (${arrivalISO})`);
  } catch (err) {
    console.error("❌ Failed to delete from manualStockOverrides:", err);
  }

  fetchShipments();
};


  // 🧮 summary cards
  const totalShipments = shipments.length;
  const delivered = shipments.filter((s) => s.status === "delivered").length;
  const inTransit = shipments.filter((s) => s.status === "in_transit").length;
  const avgDeliveryTime =
    shipments
      .filter((s) => s.delivered_date)
      .reduce(
        (acc, s) =>
          acc + (new Date(s.delivered_date) - new Date(s.ship_date)) / (1000 * 60 * 60 * 24),
        0
      ) / (shipments.filter((s) => s.delivered_date).length || 1);

  return (
    <div className="factory-page">
      <div className="header">
        <h2>🏭 Factory → Warehouse Shipments</h2>
        <div className="header-buttons">
          <button className="btn-secondary" onClick={() => navigate("/dashboard")}>
            🏠 Dashboard
          </button>
          <button className="btn-secondary" onClick={() => navigate("/shipping")}>
            🚚 Back to Shipping
          </button>
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            ➕ New Shipment
          </button>

                    <button
              className="btn-danger"
              onClick={handleBulkDelete}
              disabled={selectedIds.length === 0}
            >
              🗑️ Delete Selected ({selectedIds.length})
            </button>
          <button
            className="btn-danger"
            onClick={async () => {
              if (!window.confirm("Are you sure you want to delete ALL shipments?")) return;
              for (const s of shipments) {
                await fetch(`${API_URL}/${s.id}`, { method: "DELETE" });
              }
              setSelectedIds([]);
              fetchShipments();
            }}
          >
            ❌ Delete All
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="summary-cards">
        <div className="card total">
          <h4>Total Shipments</h4>
          <p>{totalShipments}</p>
        </div>
        <div className="card delivered">
          <h4>Delivered</h4>
          <p>{delivered}</p>
        </div>
        <div className="card transit">
          <h4>In Transit</h4>
          <p>{inTransit}</p>
        </div>
        <div className="card avg">
          <h4>Avg Delivery Time</h4>
          <p>{isNaN(avgDeliveryTime) ? "—" : `${avgDeliveryTime.toFixed(1)} days`}</p>
        </div>
      </div>

      {/* 📦 Form Modal */}
      {showForm && (
        <div className="overlay" onClick={() => setShowForm(false)}>
          <div className="form-modal" onClick={(e) => e.stopPropagation()}>
            <div className="form-header">
              <h3>{editId ? "✏️ Edit Shipment" : "📦 Create New Shipment"}</h3>
              <button className="close-btn" onClick={() => setShowForm(false)}>
                ✖
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* 📦 Serial boxes */}
              <textarea
                name="serialBoxes"
                placeholder="Paste serial boxes here (one per line)"
                value={formData.serialBoxes}
                onChange={(e) => setFormData({ ...formData, serialBoxes: e.target.value })}
              ></textarea>

              {/* 🧭 Destination */}
              <select
                value={formData.toWarehouse}
                onChange={(e) => setFormData({ ...formData, toWarehouse: e.target.value })}
              >
                <option value="">Select destination...</option>
                <option value="itb">🇺🇸 ITB (US)</option>
                <option value="tive">🇬🇧 TIVE (UK)</option>
                <option value="amazon_us">🇺🇸 Amazon US</option>
                <option value="amazon_uk">🇬🇧 Amazon UK</option>
              </select>

              {/* 🔢 Multiple SKU rows */}
              {skuList.map((item, index) => (
                <div key={index} className="sku-row">
                  <select
                    value={item.sku}
                    onChange={(e) => updateSkuRow(index, "sku", e.target.value)}
                    disabled={!formData.toWarehouse}
                  >
                    <option value="">Select SKU...</option>
                    {["itb", "amazon_us"].includes(formData.toWarehouse) && (
                      <>
                        <option value="us_cityplay_m">US CityPlay M</option>
                        <option value="us_cityplay_l">US CityPlay L</option>
                        <option value="us_20_m">US 2.0 M</option>
                        <option value="us_20_l">US 2.0 L</option>
                      </>
                    )}
                    {["tive", "amazon_uk"].includes(formData.toWarehouse) && (
                      <>
                        <option value="uk_cityplay_m">UK CityPlay M</option>
                        <option value="uk_cityplay_l">UK CityPlay L</option>
                        <option value="uk_20_m">UK 2.0 M</option>
                        <option value="uk_20_l">UK 2.0 L</option>
                      </>
                    )}
                  </select>

                  <input
                    type="number"
                    placeholder="Quantity"
                    value={item.quantity}
                    onChange={(e) => updateSkuRow(index, "quantity", e.target.value)}
                  />

                  {index === skuList.length - 1 ? (
                    <button type="button" onClick={addSkuRow}>
                      ➕
                    </button>
                  ) : (
                    <button type="button" onClick={() => removeSkuRow(index)}>
                      ➖
                    </button>
                  )}
                </div>
              ))}

              {/* 🏭 other fields */}
              <input
                type="text"
                placeholder="From Factory"
                value={formData.fromFactory}
                onChange={(e) => setFormData({ ...formData, fromFactory: e.target.value })}
              />
              <input
                type="text"
                placeholder="Shipping Company (optional)"
                value={formData.shippingCompany}
                onChange={(e) => setFormData({ ...formData, shippingCompany: e.target.value })}
              />
              <input
                type="text"
                placeholder="Tracking Number (optional)"
                value={formData.trackingNumber}
                onChange={(e) => setFormData({ ...formData, trackingNumber: e.target.value })}
              />
              <input
                type="date"
                value={formData.shipDate}
                onChange={(e) => setFormData({ ...formData, shipDate: e.target.value })}
              />

              <button type="submit" className="btn-primary">
                {editId ? "💾 Save Changes" : "✅ Create Shipment"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 📋 Table */}
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th></th>
              <th>Serial Box</th>
              <th>SKU</th>
              <th>Quantity</th>
              <th>From</th>
              <th>To</th>
              <th>Company</th>
              <th>Tracking</th>
              <th>Ship Date</th>
              <th>Delivered</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {shipments.map((row) => (
              <tr key={row.id}>
                  <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(row.id)}
                    onChange={() => toggleSelect(row.id)}
                  />
                </td>
                <td>{row.serial_box || "—"}</td>
                <td>{row.sku}</td>
                <td>{row.quantity || "—"}</td>
                <td>{row.from_factory}</td>
                <td>{row.to_warehouse}</td>
                <td>{row.shipping_company || "—"}</td>
                <td>{row.tracking_number || "—"}</td>
                <td>{row.ship_date ? new Date(row.ship_date).toLocaleDateString() : "—"}</td>
                <td>
                  {row.delivered_date ? new Date(row.delivered_date).toLocaleDateString() : "—"}
                </td>
                <td className={`status ${row.status}`}>{row.status}</td>
                <td>
                  {row.status === "in_transit" && (
                    <>
                      <button className="btn-edit" onClick={() => handleEdit(row)}>✏️ Edit</button>
                      <button className="btn-confirm" onClick={() => handleConfirm(row.id)}>
                        ✅ Confirm
                      </button>
                    </>
                  )}
                  <button className="btn-delete" onClick={() => handleDelete(row.id)}>🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
